/**
 * Firebase Configuration
 *
 * Initializes the Firebase app and authentication service.
 * Configuration values are loaded from environment variables.
 *
 * Migration Note (2026-02-22):
 * This replaces the previous MSAL configuration per Architecture Guardrail 1-006.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

/**
 * Firebase configuration object loaded from environment variables
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

/**
 * Validate that required Firebase configuration is present
 */
function validateConfig(): void {
  const missingVars: string[] = [];

  if (!firebaseConfig.apiKey) missingVars.push('VITE_FIREBASE_API_KEY');
  if (!firebaseConfig.authDomain) missingVars.push('VITE_FIREBASE_AUTH_DOMAIN');
  if (!firebaseConfig.projectId) missingVars.push('VITE_FIREBASE_PROJECT_ID');

  if (missingVars.length > 0) {
    console.warn(
      `[Firebase] Missing environment variables: ${missingVars.join(', ')}. ` +
        'Authentication may not work correctly.'
    );
  }
}

// Validate configuration on module load
validateConfig();

// Initialize Firebase app
let app: FirebaseApp;
try {
  app = initializeApp(firebaseConfig);
  console.info('[Firebase] App initialized successfully');
} catch (error) {
  console.error('[Firebase] Failed to initialize app:', error);
  throw error;
}

// Initialize Firebase Authentication
let auth: Auth;
try {
  auth = getAuth(app);
  console.info('[Firebase] Auth initialized successfully');
} catch (error) {
  console.error('[Firebase] Failed to initialize auth:', error);
  throw error;
}

export { app, auth };
